// @ts-strict-ignore
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  Input,
  NgZone,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { LMColumnsBaseComponent } from './lm-columns-base.component';
import { LMConditionUtilService } from '@utils/lm-condition-util.service';
import { LMLayoutUtilDirective } from './lm-layout-util.directive';
import { LMFormLayoutService } from '@form/services/lm-form-layout.service';
import { LMLoggerService } from '@app/services';

@Component({
  selector: 'lm-columns-grid',
  templateUrl: './lm-columns-grid.component.html',
  styleUrls: [ './lm-columns-grid.component.scss' ],
  providers: [ LMLayoutUtilDirective, LMFormLayoutService ],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class LMColumnsGridComponent extends LMColumnsBaseComponent implements OnInit, AfterViewChecked {
  public gridRows: Array<LMFormGridLayoutRow>;
  private columnWidths = new Map();
  private minWidthSet = false;

  @Input() set activatedTabChanged(activatedTabChanged) {

    if (activatedTabChanged !== undefined && this.elements !== undefined && this.elements.length > 0) {
      this.initColumnWidthMap();
      this.setMinColumnWidth();
    }
  }

  @ViewChildren('LMGridElement') elements: QueryList<ElementRef>;

  @HostBinding('class.lm-grid-distributed') distributed = false;
  @HostBinding('class.lm-grid-undistributed') undistributed = false;

  constructor(
    protected lmConditionUtilService: LMConditionUtilService,
    protected lmFormLayoutService: LMFormLayoutService,
    protected lmLoggerService: LMLoggerService,
    protected changeDetectorRef: ChangeDetectorRef,
    private ngZone: NgZone,
  ) {
    super(lmConditionUtilService, lmFormLayoutService, lmLoggerService, changeDetectorRef);
  }

  ngOnInit() {
    super.ngOnInit();

    if (this.lmColumnLayoutInfo.distributed) {
      this.distributed = true;
    } else {
      this.undistributed = false;
    }

    if (this.layoutObject.explicit) {
      this.gridRows = this.setExplicitGridRows();
    } else {
      this.gridRows = this.setImplicitGridRows();
    }

    this.initColumnWidthMap();
    this.buildElements(this.gridRows);

    // determine if a element's previous sibling is a collapsible header
    this.lmFormLayoutService.calculateGridPreviousHeaderSiblings(this.gridRows);
  }

  ngAfterViewChecked() {
    if (this.elements.toArray().length > 0 && !this.minWidthSet) {
      this.ngZone.runOutsideAngular(() => {
        setTimeout(() => {
          this.setMinColumnWidth();
          this.minWidthSet = true;
        }, 1);
      });
    }
  }

  private setMinColumnWidth() {
    const elements = this.elements.toArray();
    elements.forEach((element) => {
      const colIndex = Number(element.nativeElement.getAttribute('colindex'));
      const width = this.columnWidths.get(colIndex);
      const elementWidth = Math.floor(element.nativeElement.getBoundingClientRect().width);
      let isParagraph;

      if (element.nativeElement.children[0] && element.nativeElement.children[0].nodeName === 'LM-PARAGRAPH') {
        isParagraph = true;
      }

      if ((width === null || elementWidth > width) && !isParagraph) {
        this.columnWidths.set(colIndex, elementWidth);
      }

      if (isParagraph) {
        // lm-paragraph -> div.compound-field -> <Array of fields>
        const paragraphFields = element.nativeElement.children[0].children[0].children;
        Array.from(paragraphFields).forEach((el: HTMLElement) => {
          const elWidth = el.getBoundingClientRect().width;

          if (width === null || elWidth > width) {
            this.columnWidths.set(colIndex, elWidth + 20);
          }
        });
      }

      if (isParagraph && colIndex !== this.layoutObject.nbrColumns) {
        element.nativeElement.style.maxWidth = this.distributed ? '' : `${this.columnWidths.get(colIndex)}px`;
      } else {
        element.nativeElement.style.minWidth = this.distributed ? '' : `${this.columnWidths.get(colIndex)}px`;
      }

      if (this.distributed) {
        element.nativeElement.style.width = `${Math.floor(100 / this.layoutObject.nbrColumns)}%`;
      }
    });
  }

  private initColumnWidthMap() {
    for (let i = 0, l = 12; i < l; i++) {
      this.columnWidths.set(i, null);
    }
  }

  private setExplicitGridRows(): Array<LMFormGridLayoutRow> {
    const gridRows = [];
    let nbrOfRows = 1;
    let colIndex = 0;

    this.layoutObject.columns.forEach((column: LMFormLayoutColumn) => {
      nbrOfRows = column.elements.length > nbrOfRows ? column.elements.length : nbrOfRows;
    });

    for (let i = 0, l = nbrOfRows; i < l; i++) {
      gridRows.push({ elements: [] });
    }

    this.layoutObject.columns.forEach((column: LMFormLayoutColumn) => {
      column.elements.forEach((element: LMFormLayoutElement, index) => {
        element.colIndex = colIndex;
        element.class = `lm-grid-element lm-grid-col-${this.layoutObject.nbrColumns}`;
        gridRows[index].elements.push(element);
      });

      colIndex++;
    });

    return gridRows;
  }

  private setImplicitGridRows(): Array<LMFormGridLayoutRow> {
    const nbrColumns = this.layoutObject.nbrColumns;

    const gridRows = [];
    const elements: Array<LMFormLayoutElement> = this.getElements();
    const nbrOfElements = elements.length;
    const nbrOfRows = this.getNumberOfRows(nbrOfElements);
    let rowIndex = 0;

    for (let i = 0, l = nbrOfRows; i < l; i++) {
      gridRows.push({ elements: [] });
    }

    elements.forEach((element, index) => {
      gridRows[rowIndex].elements.push(element);
      element.colIndex = gridRows[rowIndex].elements.length;
      element.class = `lm-grid-element lm-grid-col-${nbrColumns}`;

      if (index !== 0 && ((index + 1) % nbrColumns === 0)) {
        rowIndex++;
      }
    });

    return gridRows;
  }

  private getElements(): Array<LMFormLayoutElement> {
    const elements = [];
    this.layoutObject.columns.forEach((column) => {
      column.elements.forEach((element) => elements.push(element));
    });

    return elements;
  }

  private getNumberOfRows(nbrOfElements: number): number {
    const quotient = nbrOfElements / this.layoutObject.nbrColumns;
    const hasRemainder = nbrOfElements / this.layoutObject.nbrColumns % 1 !== 0;

    return hasRemainder ? Math.floor(quotient + 1) : quotient;
  }
}
